import React, { useEffect, useState } from 'react';
import {
  Modal,
  Table,
  Tag,
  Form,
  Input,
  Radio,
  Spin,
  Alert,
  Space,
  message
} from 'antd';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import CustomButton from '@/common/CustomButton';
import apiClient from 'utilities/apiClient';
import FormItem from '@/common/FormItem';
import { getTestRequestCycles, closeTestRequestCycle } from '../../redux/slice';

const { TextArea } = Input;

/**
 * Every test request a batch has run, and the way to close the open one.
 *
 * A batch runs several cycles over its life and only one may be open at a time,
 * so the warehouse is blocked from raising the next one until the current one is
 * closed. This is where a QC Level 3 user does that.
 *
 * Closing is deliberately not the same act as rejecting. A rejected test records
 * that the material failed specification; closing records that the cycle was
 * abandoned without a result. They print differently on a regulated document, so
 * they are kept apart.
 */
const DeactivateTrfModal = ({
  visible,
  batchDetailsId,
  onCancel,
  onChanged,
  /* View-only. The warehouse opens this from the line-clearance list purely to
     see which cycles the batch has run and which one its clearance belongs to.
     Closing a request is a QC Level 3 act (enforced in closeTestRequest), so the
     action column is dropped entirely rather than rendered and then refused. */
  readOnly = false
}) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState(null);
  /* The request being closed. null = showing the list. */
  const [closingRequest, setClosingRequest] = useState(null);
  const [disposition, setDisposition] = useState('returned');
  /* id of the request currently being re-printed, so only that row's button
     spins rather than every row at once. */
  const [reprintingId, setReprintingId] = useState(null);

  /* Re-prints ONE cycle, rendered from that request as it stands now rather than
     from the blob stored when it was raised - cycle 1's blob was written before
     QC entered its AR, so it shows NA where the AR should be.
     Routed through apiClient.getBlob because /trf is behind verify_auth: a plain
     window.open carries no token and gets a 403. */
  const handleReprint = (record) => {
    setReprintingId(record?.id);
    apiClient
      .getBlob(
        `${process.env.REACT_APP_API_URL}/trf/create-trf-pdf`,
        { batch_details_id: batchDetailsId, test_request_form_id: record?.id },
        true
      )
      .then((blob) => {
        setReprintingId(null);
        if (!(blob instanceof Blob)) {
          message.error('Could not re-print this test request.');
          return;
        }
        const url = window.URL.createObjectURL(
          new Blob([blob], { type: 'application/pdf' })
        );
        window.open(url, '_blank');
        setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      })
      .catch(() => {
        setReprintingId(null);
        message.error('Could not re-print this test request.');
      });
  };

  const load = () => {
    if (!batchDetailsId) return;
    setLoading(true);
    dispatch(getTestRequestCycles({ batch_details_id: batchDetailsId })).then(
      (response) => {
        setLoading(false);
        if (response?.payload?.success) {
          setData(response?.payload?.data);
        }
      }
    );
  };

  useEffect(() => {
    if (visible) {
      setClosingRequest(null);
      form.resetFields();
      load();
    }
  }, [visible, batchDetailsId]);

  const handleClose = (values) => {
    setSubmitting(true);
    dispatch(
      closeTestRequestCycle({
        test_request_form_id: closingRequest?.id,
        batch_details_id: batchDetailsId,
        employee_id: values?.employee_id,
        password: values?.password,
        reason: values?.reason,
        /* Only sent when the cycle actually deducted something. The backend asks
           for it rather than assuming, because a sample is sometimes physically
           consumed even though the cycle was abandoned, and inventing the stock
           back would be worse than leaving it deducted. */
        stock_disposition: closingRequest?.deducted_quantity
          ? disposition
          : undefined,
        stock_disposition_reason:
          disposition === 'left_deducted'
            ? values?.stock_disposition_reason
            : undefined
      })
    ).then((response) => {
      setSubmitting(false);
      if (response?.payload?.success) {
        message.success(response?.payload?.message);
        setClosingRequest(null);
        form.resetFields();
        load();
        if (onChanged) onChanged();
      } else {
        message.error(response?.payload?.message);
      }
    });
  };

  const statusTag = (record) => {
    if (record?.is_legacy) {
      /* Raised before cycle tracking existed. Neither open nor closed - it simply
         predates the concept, and it never blocks a new cycle. */
      return <Tag color="#6c757d">LEGACY</Tag>;
    }
    const colour =
      record?.request_status === 'Completed'
        ? '#28a745'
        : record?.request_status === 'Abandoned'
        ? '#dc3545'
        : record?.request_status === 'Under Test'
        ? '#343a40'
        : '#007bff';
    return <Tag color={colour}>{record?.request_status?.toUpperCase()}</Tag>;
  };

  const columns = [
    {
      title: '#',
      dataIndex: 'cycle_no',
      key: 'cycle_no',
      width: '50px',
      render: (text) => text || '-'
    },
    {
      title: 'Test',
      dataIndex: 'test_requested',
      key: 'test_requested',
      ellipsis: true,
      render: (text) => text || 'NA'
    },
    {
      title: 'Serial',
      dataIndex: 'trf_serial',
      key: 'trf_serial',
      width: '80px',
      render: (text) => text || 'NA'
    },
    {
      title: 'AR Number',
      dataIndex: 'ar_no',
      key: 'ar_no',
      ellipsis: true,
      /* Empty until QC enters it at the decision - it does not exist when the
         request is raised. */
      render: (text) => text || '—'
    },
    {
      title: 'Status',
      key: 'request_status',
      width: '110px',
      render: (_, record) => statusTag(record)
    },
    {
      title: 'Result',
      dataIndex: 'analytical_status',
      key: 'analytical_status',
      width: '90px',
      render: (text) => text || '—'
    },
    {
      title: 'Raised',
      dataIndex: 'raised_at',
      key: 'raised_at',
      width: '110px',
      render: (text) => (text ? moment(text).format('DD-MMM-YYYY') : 'NA')
    },
    {
      /* Always rendered now: re-printing is a read action and every cycle has a
         form worth printing, including closed ones. Close keeps its old
         conditions - open requests only, and never in the read-only view. */
      title: '',
      key: 'action',
      width: '180px',
      render: (_, record) => (
        <Space>
          <CustomButton
            size="small"
            type="primary"
            ghost
            loading={reprintingId === record?.id}
            onClick={() => handleReprint(record)}
          >
            Re-print
          </CustomButton>
          {!readOnly && record?.is_open ? (
            <CustomButton
              size="small"
              type="primary"
              onClick={() => {
                setClosingRequest(record);
                setDisposition('returned');
                form.resetFields();
              }}
            >
              Close
            </CustomButton>
          ) : null}
        </Space>
      )
    }
  ].filter(Boolean);

  const expanded = (record) => (
    <div style={{ paddingLeft: '12px' }}>
      <div>
        <strong>Line clearance:</strong>{' '}
        {record?.line_clearance
          ? `${record?.line_clearance?.booth || 'NA'} · ${
              record?.line_clearance?.date
                ? moment(record?.line_clearance?.date).format('DD-MMM-YYYY')
                : 'NA'
            }`
          : '— not linked to this cycle'}
      </div>
      <div>
        <strong>Sampling:</strong>{' '}
        {record?.sampling?.length
          ? `${record?.sampling?.length} container(s)`
          : record?.is_legacy
          ? '— recorded before cycle tracking, not attributed to a request'
          : '— none yet'}
      </div>
      {record?.sampling?.length ? (
        <div style={{ paddingLeft: '12px' }}>
          {record?.sampling?.map((s) => (
            <div key={s?.id}>
              {s?.container || 'NA'} — {parseFloat(s?.quantity)} {s?.uom || ''}
            </div>
          ))}
        </div>
      ) : null}
      <div>
        <strong>Stock deducted:</strong>{' '}
        {record?.deducted_quantity === null ||
        record?.deducted_quantity === undefined
          ? '— not attributable to this cycle'
          : record?.deducted_quantity}
      </div>
      {record?.close_reason ? (
        <div>
          <strong>Closed:</strong> {record?.closed_by || 'NA'} —{' '}
          {record?.close_reason}
          {record?.stock_disposition
            ? ` (stock ${
                record?.stock_disposition === 'returned'
                  ? 'returned'
                  : 'left deducted'
              })`
            : ''}
        </div>
      ) : null}
    </div>
  );

  return (
    <Modal
      title={
        closingRequest
          ? `Close test request #${closingRequest?.cycle_no || ''}`
          : `Test Requests — ${data?.batch?.inhouse_batch_number || ''}`
      }
      visible={visible}
      onCancel={onCancel}
      footer={null}
      width={closingRequest ? 620 : 1000}
      destroyOnClose
    >
      {loading ? (
        <Spin />
      ) : closingRequest ? (
        <Form form={form} layout="vertical" onFinish={handleClose}>
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: '12px' }}
            message={`${closingRequest?.test_requested || 'Test'} · ${
              closingRequest?.request_status
            }`}
            description="Closing abandons this cycle without recording a result. The batch returns to Quarantine and needs a fresh line clearance before the next request."
          />
          <FormItem
            label="User ID"
            name="employee_id"
            rules={[{ required: true, message: 'Enter User ID' }]}
          >
            <Input placeholder="Enter User ID" />
          </FormItem>
          <FormItem
            label="Password"
            name="password"
            rules={[{ required: true, message: 'Enter Password' }]}
          >
            <Input.Password placeholder="Enter Password" />
          </FormItem>
          <FormItem
            label="Reason"
            name="reason"
            rules={[{ required: true, message: 'A reason is required' }]}
          >
            <TextArea rows={3} placeholder="Why is this cycle being closed?" />
          </FormItem>

          {closingRequest?.deducted_quantity ? (
            <>
              <FormItem
                label={`This request deducted ${closingRequest?.deducted_quantity} from the batch`}
              >
                <Radio.Group
                  value={disposition}
                  onChange={(e) => setDisposition(e.target.value)}
                >
                  <Radio value="returned">Return it to the batch</Radio>
                  <Radio value="left_deducted">
                    Leave it deducted — material was consumed or destroyed
                  </Radio>
                </Radio.Group>
              </FormItem>
              {disposition === 'left_deducted' ? (
                <FormItem
                  label="Justification"
                  name="stock_disposition_reason"
                  rules={[
                    {
                      required: true,
                      message:
                        'Leaving material deducted is a deliberate stock discrepancy and needs its own justification'
                    }
                  ]}
                >
                  <TextArea
                    rows={2}
                    placeholder="Why is the material not being returned?"
                  />
                </FormItem>
              ) : null}
            </>
          ) : (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: '12px' }}
              message="No stock to return"
              description="This request has no stock movement attributed to it, so nothing is returned on closing."
            />
          )}

          <div style={{ textAlign: 'right' }}>
            <CustomButton onClick={() => setClosingRequest(null)}>
              Back
            </CustomButton>{' '}
            <CustomButton type="primary" htmlType="submit" loading={submitting}>
              Close Request
            </CustomButton>
          </div>
        </Form>
      ) : (
        <>
          {data?.has_open_request ? (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: '12px' }}
              message="One request is open"
              description="A new cycle cannot be raised until it is closed."
            />
          ) : null}
          <Table
            rowKey="id"
            columns={columns}
            dataSource={data?.test_requests || []}
            pagination={false}
            size="small"
            expandable={{ expandedRowRender: expanded }}
          />
        </>
      )}
    </Modal>
  );
};

export default DeactivateTrfModal;
